import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, X, Check } from 'lucide-react';

const SearchableSelect = ({ 
  selectElement,
  disabled,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const updateOptions = () => {
    if (!selectElement) return;
    
    const opts = Array.from(selectElement.options).map(opt => ({
      value: opt.value,
      label: opt.text,
      disabled: opt.disabled
    }));
    
    setOptions(opts.filter(opt => opt.value !== ''));
  };

  useEffect(() => {
    if (!selectElement) return;
    
    updateOptions();
    
    const observer = new MutationObserver(updateOptions);
    observer.observe(selectElement, { 
      childList: true, 
      subtree: true,
      attributes: true,
      characterData: true
    });
    
    return () => observer.disconnect();
  }, [selectElement]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const filteredOptions = options.filter(option => {
    if (!searchTerm) return true;
    return option.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const currentValue = selectElement?.value || '';
  const selectedOption = options.find(opt => opt.value === currentValue);
  const placeholder = selectElement?.options[0]?.text || 'Selecione...';

  const handleSelectOption = (optionValue) => {
    if (selectElement) {
      selectElement.value = optionValue;
      
      const changeEvent = new Event('change', { bubbles: true });
      const inputEvent = new Event('input', { bubbles: true });
      
      selectElement.dispatchEvent(changeEvent);
      selectElement.dispatchEvent(inputEvent);
    }
    
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    handleSelectOption('');
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-4 py-2.5 border rounded-lg text-left
          flex items-center justify-between
          focus:ring-2 focus:border-transparent
          transition-colors
          border-gray-300 dark:border-gray-600 focus:ring-blue-500
          ${disabled 
            ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-60' 
            : 'bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500'
          }
          ${className}
        `}
      >
        <span className={`truncate ${selectedOption ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        
        <div className="flex items-center gap-2 ml-2">
          {selectedOption && !disabled && (
            <X
              className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={handleClear}
            />
          )}
          <ChevronDown 
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-80 overflow-hidden">
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                         dark:bg-gray-700 dark:text-white text-sm"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                Nenhuma opção encontrada
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = option.value === currentValue;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelectOption(option.value)}
                    disabled={option.disabled}
                    className={`
                      w-full px-4 py-2.5 text-left flex items-center justify-between
                      transition-colors
                      ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                      ${isSelected 
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' 
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'
                      }
                    `}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && <Check className="w-4 h-4 ml-2 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;